import React, { useState, useEffect } from 'react';
import { Button, Card, Table, Badge, Spinner, Alert, Modal, Form, Row, Col } from 'react-bootstrap';
import { tripService, UPLOADS_BASE_URL } from '../../services/api';
import { format } from 'date-fns';

const TripManagement = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    startDate: '',
    endDate: '',
    capacity: 10,
    minimumCertification: 'OW',
    description: '',
    tags: '',
    price: '',
    currency: 'EUR'
  });
  const [formError, setFormError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [tripImages, setTripImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  // Fetch all trips
  const fetchTrips = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tripService.getAllTrips();
      setTrips(response.data);
    } catch (err) {
      console.error('Error fetching trips:', err);
      setError('Failed to load trips. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle multiple image selection and preview
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const currentTotal = (existingImages.length || 0) + (tripImages.length || 0);
    const remaining = 10 - currentTotal;
    
    if (remaining <= 0) {
      setFormError('Maximum 10 images allowed per trip');
      return;
    }
    
    const newImages = files.slice(0, remaining);
    setTripImages(prev => [...prev, ...newImages]);
    
    // Create previews
    newImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove new image before upload
  const removeNewImage = (index) => {
    setTripImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Delete existing image from trip
  const deleteExistingImage = async (tripId, filename) => {
    try {
      await tripService.deleteImage(tripId, filename);
      setExistingImages(prev => prev.filter(img => img !== filename));
      await fetchTrips();
    } catch (err) {
      console.error('Error deleting image:', err);
      setFormError('Failed to delete image');
    }
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    // Reset form
    setFormData({
      name: '',
      location: '',
      startDate: '',
      endDate: '',
      capacity: 10,
      minimumCertification: 'OW',
      description: '',
      tags: '',
      price: '',
      currency: 'EUR'
    });
    setTripImages([]);
    setImagePreviews([]);
    setExistingImages([]);
    setFormError(null);
    setShowCreateModal(true);
  };

  // Open edit modal
  const handleOpenEditModal = (trip) => {
    setSelectedTrip(trip);
    setFormData({
      name: trip.name,
      location: trip.location,
      startDate: trip.startDate.split('T')[0], // Format date for input
      endDate: trip.endDate.split('T')[0], // Format date for input
      capacity: trip.capacity,
      minimumCertification: trip.minimumCertification,
      description: trip.description,
      tags: trip.tags ? trip.tags.join(', ') : '',
      status: trip.status || 'upcoming',
      price: trip.price || '',
      currency: trip.currency || 'EUR'
    });
    setTripImages([]);
    setImagePreviews([]);
    setExistingImages(trip.images || []);
    setFormError(null);
    setShowEditModal(true);
  };

  // Open delete modal
  const handleOpenDeleteModal = (trip) => {
    setSelectedTrip(trip);
    setShowDeleteModal(true);
  };

  // Create new trip
  const handleCreateTrip = async (e) => {
    e.preventDefault();
    
    try {
      setFormError(null);
      
      // Validate dates
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        setFormError('Start date cannot be after end date');
        return;
      }
      
      // Create FormData object for file upload
      const tripData = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        tripData.append(key, formData[key]);
      });
      
      // Add images if they exist
      if (tripImages.length > 0) {
        console.log('Adding images to FormData:', tripImages.length);
        tripImages.forEach((image, index) => {
          tripData.append('tripImages', image);
          console.log(`Added image ${index + 1}:`, image.name);
        });
      }
      
      // IMPORTANT: Don't set Content-Type here - let the browser handle it
      const response = await tripService.createTrip(tripData);
      
      console.log('Trip created successfully:', response.data);
      
      // Success - close modal and refresh trips
      setShowCreateModal(false);
      setActionSuccess('Trip created successfully!');
      fetchTrips();
      
      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error creating trip:', err);
      setFormError(err.response?.data?.message || 'Failed to create trip. Please try again.');
    }
  };

  // Update existing trip
  const handleUpdateTrip = async (e) => {
    e.preventDefault();
    
    if (!selectedTrip) return;
    
    try {
      setFormError(null);
      
      // Validate dates
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        setFormError('Start date cannot be after end date');
        return;
      }
      
      // Create FormData object for file upload
      const tripData = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        tripData.append(key, formData[key]);
      });
      
      // Add new images if they exist
      if (tripImages.length > 0) {
        console.log('Adding new images to update:', tripImages.length);
        tripImages.forEach((image, index) => {
          tripData.append('tripImages', image);
          console.log(`Added new image ${index + 1}:`, image.name);
        });
      }

      // Send API request with FormData
      await tripService.updateTrip(selectedTrip._id, tripData);
      
      // Success - close modal and refresh trips
      setShowEditModal(false);
      setActionSuccess('Trip updated successfully!');
      fetchTrips();
      
      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error updating trip:', err);
      setFormError(err.response?.data?.message || 'Failed to update trip. Please try again.');
    }
  };

  // Delete trip
  const handleDeleteTrip = async () => {
    if (!selectedTrip) return;
    
    try {
      console.log("Deleting trip:", selectedTrip._id); // Add this log
      
      await tripService.deleteTrip(selectedTrip._id);
      
      // Success - close modal and refresh trips
      setShowDeleteModal(false);
      setActionSuccess('Trip deleted successfully!');
      fetchTrips();
      
      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error deleting trip:', err);
      setError('Failed to delete trip. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="section-header">Trip Management</h3>
        <Button variant="primary" onClick={handleOpenCreateModal}>
          <i className="bi bi-plus-circle me-2"></i> Create New Trip
        </Button>
      </div>
      
      {actionSuccess && (
        <Alert variant="success" className="mb-4">
          {actionSuccess}
        </Alert>
      )}
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      {trips.length === 0 ? (
        <Card className="text-center p-4">
          <Card.Body>
            <p className="mb-0">No trips created yet. Click "Create New Trip" to add your first trip.</p>
          </Card.Body>
        </Card>
      ) : (
        <Table responsive striped hover className="admin-table">
          <thead>
            <tr>
              <th>Trip Name</th>
              <th>Location</th>
              <th>Dates</th>
              <th>Capacity</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {trips.map(trip => (
              <tr key={trip._id}>
                <td>{trip.name}</td>
                <td>{trip.location}</td>
                <td>
                  {format(new Date(trip.startDate), 'MMM d, yyyy')} - 
                  {format(new Date(trip.endDate), 'MMM d, yyyy')}
                </td>
                <td>
                  {trip.approvedCount || 0}/{trip.capacity}
                </td>
                <td>
                  <Badge bg={
                    trip.status === 'upcoming' ? 'primary' :
                    trip.status === 'ongoing' ? 'success' :
                    trip.status === 'completed' ? 'secondary' : 'danger'
                  }>
                    {trip.status.charAt(0).toUpperCase() + trip.status.slice(1)}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleOpenEditModal(trip)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleOpenDeleteModal(trip)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      
      {/* Create Trip Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Trip</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTrip} encType="multipart/form-data">
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            
            <Form.Group className="mb-3">
              <Form.Label>Trip Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Capacity</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Certification</Form.Label>
                  <Form.Select
                    name="minimumCertification"
                    value={formData.minimumCertification}
                    onChange={handleChange}
                    required
                  >
                    <option value="OW">Open Water</option>
                    <option value="AOW">Advanced Open Water</option>
                    <option value="Rescue">Rescue Diver</option>
                    <option value="DM">Divemaster</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Trip Images</Form.Label>
              
              {/* Existing Images (Edit Mode Only) */}
              {selectedTrip && existingImages.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Current Images ({existingImages.length}/10)</small>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                    {existingImages.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: '8px', overflow: 'hidden', border: '2px solid #dee2e6' }}>
                        <img 
                          src={`${UPLOADS_BASE_URL}/uploads/${img}`} 
                          alt={`Trip ${idx + 1}`} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.src = '/images/default-trip.jpg'; }}
                        />
                        <button
                          type="button"
                          onClick={() => deleteExistingImage(selectedTrip._id, img)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(220, 53, 69, 0.9)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            fontSize: '20px',
                            lineHeight: '1',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Delete image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">New Images to Upload ({imagePreviews.length})</small>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                    {imagePreviews.map((preview, idx) => (
                      <div key={idx} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: '8px', overflow: 'hidden', border: '2px solid #0077b6' }}>
                        <img 
                          src={preview} 
                          alt={`Preview ${idx + 1}`} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          onClick={() => removeNewImage(idx)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(220, 53, 69, 0.9)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            fontSize: '20px',
                            lineHeight: '1',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Input */}
              <Form.Control
                type="file"
                name="tripImages"
                onChange={handleImageChange}
                accept="image/*"
                multiple
                disabled={(existingImages.length + tripImages.length) >= 10}
              />
              <Form.Text className="text-muted">
                {(existingImages.length + tripImages.length) >= 10 
                  ? 'Maximum 10 images reached' 
                  : `Select multiple images (${10 - (existingImages.length + tripImages.length)} slots remaining). Max 10 images total, 5MB each.`
                }
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags (comma separated)</Form.Label>
              <Form.Control
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="e.g. Reef, Wreck, Warm Water"
              />
              <Form.Text className="text-muted">
                Optional: Add tags to categorize your trip
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Price *</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="500.00"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Trip
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* Edit Trip Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Trip</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateTrip}>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            
            <Form.Group className="mb-3">
              <Form.Label>Trip Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Capacity</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Certification</Form.Label>
                  <Form.Select
                    name="minimumCertification"
                    value={formData.minimumCertification}
                    onChange={handleChange}
                    required
                  >
                    <option value="OW">Open Water</option>
                    <option value="AOW">Advanced Open Water</option>
                    <option value="Rescue">Rescue Diver</option>
                    <option value="DM">Divemaster</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tags (comma separated)</Form.Label>
              <Form.Control
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="e.g. Reef, Wreck, Warm Water"
              />
            </Form.Group>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Price *</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="500.00"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Trip Images</Form.Label>
              
              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Current Images ({existingImages.length}/10)</small>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                    {existingImages.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: '8px', overflow: 'hidden', border: '2px solid #dee2e6' }}>
                        <img 
                          src={`${UPLOADS_BASE_URL}/uploads/${img}`} 
                          alt={`Trip ${idx + 1}`} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.src = '/images/default-trip.jpg'; }}
                        />
                        <button
                          type="button"
                          onClick={() => deleteExistingImage(selectedTrip._id, img)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(220, 53, 69, 0.9)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            fontSize: '20px',
                            lineHeight: '1',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Delete image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">New Images to Upload ({imagePreviews.length})</small>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                    {imagePreviews.map((preview, idx) => (
                      <div key={idx} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: '8px', overflow: 'hidden', border: '2px solid #0077b6' }}>
                        <img 
                          src={preview} 
                          alt={`Preview ${idx + 1}`} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          onClick={() => removeNewImage(idx)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(220, 53, 69, 0.9)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            fontSize: '20px',
                            lineHeight: '1',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Input */}
              <Form.Control
                type="file"
                name="tripImages"
                onChange={handleImageChange}
                accept="image/*"
                multiple
                disabled={(existingImages.length + tripImages.length) >= 10}
              />
              <Form.Text className="text-muted">
                {(existingImages.length + tripImages.length) >= 10 
                  ? 'Maximum 10 images reached' 
                  : `Select multiple images (${10 - (existingImages.length + tripImages.length)} slots remaining). Max 10 images total, 5MB each.`
                }
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status || 'upcoming'}
                onChange={handleChange}
                required
              >
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
            
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Trip
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the trip <strong>{selectedTrip?.name}</strong>?</p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteTrip}>
            Delete Trip
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TripManagement;